import { AzzurroHubDataType, ConnextData, MonitoringType, MonitorPrefEnumType, System } from 'ngx-core-services';

import { SystemsState, SystemDefinition } from '../state/system.state';

export function requestToAddASystem(state: SystemsState, systemId: string, observerKey: string): SystemsState {
  return {
    ...state,
    observers: {
      ...state.observers,
      [systemId]: {
        ...state.observers[systemId],
        [observerKey]: (new Date()).getTime(),
      },
    },
  };
}

export function addSystem(state: SystemsState, systemId: string, system: System): SystemsState {
  const newSystem: SystemDefinition = {
    ...system,
    subscription: 'pending',
    monitoringType: getMonitoringType(system),
    monitorPref: getMonitoringPref(system),
  };

  return {
    ...state,
    systemList: {
      ...state.systemList,
      [systemId]: newSystem,
    },
  };
}

export function removeSystem(state: SystemsState, systemId: string): SystemsState {
  const { [systemId]: removed, ...rest } = state.systemList;
  return {
    ...state,
    systemList: rest
  };
}

export function updateSystem(state: SystemsState, systemId: string, changes: Partial<SystemDefinition>): SystemsState {
  const existingSystem = state.systemList[systemId];
  const monitoringType = getMonitoringType(changes);
  const monitorPref = getMonitoringPref(changes);

  let updatedSystem: SystemDefinition;

  switch (monitoringType) {
    case 'Connext':
      updatedSystem = {
        ...(existingSystem as Extract<SystemDefinition, { monitoringType: 'Connext' }>),
        ...changes,
        monitoringType: 'Connext',
        monitorPref,
        data: changes.data as Partial<ConnextData> | undefined,
      };
      break;
    case 'AzzurroHub':
      updatedSystem = {
        ...(existingSystem as Extract<SystemDefinition, { monitoringType: 'AzzurroHub' }>),
        ...changes,
        monitoringType: 'AzzurroHub',
        monitorPref,
        data: changes.data as Partial<AzzurroHubDataType> | undefined,
      };
      break;
    case 'Solarman':
      updatedSystem = {
        ...(existingSystem as Extract<SystemDefinition, { monitoringType: 'Solarman' }>),
        ...changes,
        monitoringType: 'Solarman',
        monitorPref,
        data: undefined,
      };
      break;
    default:
      updatedSystem = {
        ...existingSystem,
        ...changes,
        monitoringType,
        monitorPref,
        data: undefined,
      } as SystemDefinition;
  }

  return {
    ...state,
    systemList: {
      ...state.systemList,
      [systemId]: updatedSystem,
    },
  };
}

export function updateConnextData(state: SystemsState, systemId: string, changes: Partial<ConnextData>): SystemsState {
  const existingSystem = state.systemList[systemId];
  if (!existingSystem || existingSystem.monitoringType !== 'Connext') {
    return state;
  }

  const connextSystem = existingSystem as Extract<SystemDefinition, { monitoringType: 'Connext' }>;

  const updatedSystem = {
    ...connextSystem,
    data: {
      ...connextSystem.data,
      ...changes
    }
  };

  return {
    ...state,
    systemList: {
      ...state.systemList,
      [systemId]: updatedSystem
    }
  };
}

export function updateAzzurroHubData(state: SystemsState, systemId: string, changes: Partial<AzzurroHubDataType>): SystemsState {
  const existingSystem = state.systemList[systemId];
  if (!existingSystem || existingSystem.monitoringType !== 'AzzurroHub') {
    return state;
  }

  const connextSystem = existingSystem as Extract<SystemDefinition, { monitoringType: 'AzzurroHub' }>;

  const updatedSystem = {
    ...connextSystem,
    data: {
      ...connextSystem.data,
      ...changes
    }
  };

  return {
    ...state,
    systemList: {
      ...state.systemList,
      [systemId]: updatedSystem
    }
  };
}

export function updateCurrentObservers(state: SystemsState, systemId: string, observerKey: string, operation: 'add' | 'remove'): SystemsState {
  if (operation === 'add') {
    return {
      ...state,
      observers: {
        ...state.observers,
        [systemId]: {
          ...state.observers[systemId],
          [observerKey]: (new Date()).getTime(),
        },
      },
    };
  }

  const { [observerKey]: removed, ...rest } = state.observers[systemId];
  return {
    ...state,
    observers: {
      ...state.observers,
      [systemId]: rest,
    },
  };
}

export function setSystemSubscribed(state: SystemsState, systemId: string): SystemsState {
  const existingSystem = state.systemList[systemId];
  if (!existingSystem) return state;
  return {
    ...state,
    systemList: {
      ...state.systemList,
      [systemId]: {
        ...existingSystem,
        subscription: 'subscribed' as const,
      }
    }
  };
}

export function setSystemUnsubscribed(state: SystemsState, systemId: string): SystemsState {
  const existingSystem = state.systemList[systemId];
  if (!existingSystem) return state;
  return {
    ...state,
    systemList: {
      ...state.systemList,
      [systemId]: {
        ...existingSystem,
        subscription: 'unsubscribed' as const,
      }
    }
  };
}

export const getMonitoringType = (changes: Partial<SystemDefinition>): MonitoringType =>{
  const source = changes.source ?? 'firebase';
  if (source === 'firebase') {
    return changes.configuration?.connextId ? 'Connext' : 'Solarman';
  }

  const hasConnext = changes.configuration?.connextId;
  const hasAzzurroHub = changes.configuration?.azzurroHubId;

  const monitorPref = (changes.monitorPref) ??
    (hasConnext ? MonitorPrefEnumType.CONNEXT :
    hasAzzurroHub ? MonitorPrefEnumType.AZZURRO_HUB :
    MonitorPrefEnumType.SOLARMAN);

  return monitorPref === MonitorPrefEnumType.CONNEXT ? 'Connext' :
          monitorPref === MonitorPrefEnumType.AZZURRO_HUB ? 'AzzurroHub' : 'Solarman';
}

export const getMonitoringPref = (changes: Partial<SystemDefinition>): MonitorPrefEnumType =>{
  const source = changes.source ?? 'firebase';
  if (source === 'firebase') {
    return changes.configuration?.connextId ? MonitorPrefEnumType.CONNEXT : MonitorPrefEnumType.SOLARMAN;
  }

  if (!changes.aws?.plantPref?.monitorPref) {
    if (changes.configuration?.connextId) {
      return MonitorPrefEnumType.CONNEXT;
    }

    if (changes.configuration?.azzurroHubId) {
      return MonitorPrefEnumType.AZZURRO_HUB;
    }
  }

  return changes.aws?.plantPref?.monitorPref ?? MonitorPrefEnumType.SOLARMAN;
}
